import { SxProps, Theme, useMediaQuery } from '@mui/material';
import {
  DataGridProProps,
  GridRowHeightParams,
  GridSlotsComponentsProps,
} from '@mui/x-data-grid-pro';

export type AssessmentDataGridProps = {
  rowHeight: number;
  columnsToPin: string[];
  withDataGridHover: boolean;
  noNavigationOnFirstCol: boolean;
  headerCellSelector: string;
};

export type MarkbookForGroupsDataGridProps = {
  getRowHeight: DataGridProProps['getRowHeight'];
} & AssessmentDataGridProps;

export type AssessmentScrollConfig = {
  threshold: number;
  colWidth: number;
};

export const ASSESSMENT_COL_WIDTH = 112;
export const ASSESSMENT_MIN_COL_SPAN_WIDTH = ASSESSMENT_COL_WIDTH * 2;
export const ASSESSMENT_CELL_HEIGHT = 44;
export const ASSESSMENT_ROW_HEIGHT = 45;
export const STUDENT_COL_FIELD_KEY = 'students';
export const BASELINE_COL_FIELD_KEY = 'baseline';
export const AVERAGE_COL_FIELD_KEY = 'average';
export const MARKBOOK_FOR_GROUP_STUDENTS_HEADER_HEIGHT = 63;
export const MARKBOOK_FOR_GROUP_METHOD_TYPE_HEADER_HEIGHT = 32;
export const MARKBOOK_FOR_GROUPS_STUDENTS_COL_WIDTH = 255;
export const MARKBOOK_FOR_ASSESSMENT_PREVIEW_STUDENTS_COL_WIDTH = 250;
const MARKBOOK_FOR_GROUPS_SCROLL_ARROWS_HEIGHT = 99;
const MARKBOOK_FOR_GROUPS_SCROLL_ARROWS_WIDTH = 32;

export const getAssessmentSubColWidth = (subColumns: number) =>
  subColumns === 1 ? ASSESSMENT_MIN_COL_SPAN_WIDTH : ASSESSMENT_COL_WIDTH;
export const getAssessmentColumnWidth = (subColumns: number) =>
  getAssessmentSubColWidth(subColumns) * subColumns;

export const ASSESSMENT_GRID_SLOT_PROPS: GridSlotsComponentsProps = {
  cell: {
    onKeyDown: (e: React.KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
          e.stopPropagation();
          break;
      }
    },
  },
};

export const ASSESSMENT_CSS_CLASSES = {
  gridContainer: 'gridContainer',
  studentNameCell: 'studentNameCell',
  scoreCell: 'scoreCell',
  gradeCell: 'gradeCell',
  commentCell: 'commentCell',
  assessmentInfoHeaderCell: 'assessmentInfoHeaderCell',
  categoryNameCell: 'categoryNameCell',
  studentsHeaderCell: 'studentsHeaderCell',
  subjectHeaderCell: 'subjectHeaderCell',
  withRightBorder: 'withRightBorder',
  withTopBorder: 'withTopBorder',
  withBottomBorder: 'withBottomBorder',
  methodTypeCell: 'methodTypeCell',
  overflowVisible: 'overflowVisible',
  loadMoreCell: 'loadMoreCell',
  loadingNextPageCell: 'loadingNextPageCell',
  baselineHeaderCell: 'baselineHeaderCell',
  averageHeaderCell: 'averageHeaderCell',
  averageCell: 'averageCell',
  averageCategoryCell: 'averageCategoryCell',
} as const;

export const useMarkbookForGroupStudentNameCellWidth = (): number => {
  const belowLargeScreenWidth = useMediaQuery((theme: Theme) => theme.breakpoints.down('lg'));
  return belowLargeScreenWidth ? 307 : 282;
};

export const createStudentNameCellSx = (): SxProps<Theme> => ({
  maxWidth: '100%',
  '& .card-body': {
    maxHeight: ASSESSMENT_CELL_HEIGHT,
  },
});

export const createMarkbookForGroupsStudentNameCellSx = (): SxProps<Theme> => ({
  maxWidth: '100%',
  '& .card-body': {
    maxHeight: ASSESSMENT_CELL_HEIGHT,
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
});

export const createMarkbookForGroupGridContainerSx = (): SxProps<Theme> => (theme: Theme) => ({
  m: 3,
  mt: 0,
  width: '95%',
  borderRadius: 0,
  [theme.breakpoints.down('md')]: {
    mr: 0,
    width: '94%',
  },
});

export const createMarkbookForGroupsGridContainerSx = (): SxProps<Theme> => (theme: Theme) => ({
  borderRadius: 0,
  borderTop: theme.mixins.borderValue(),
  borderRight: theme.mixins.borderValue(),
  borderBottom: theme.mixins.borderValue(),
  overflow: 'auto',
  maxWidth: '100%',
  maxHeight: '100%',
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  '& .MuiDataGrid-pinnedRows': {
    backgroundColor: theme.palette.background.paper,
  },
  '.MuiStack-root.arrowLeft': {
    left: `${
      MARKBOOK_FOR_GROUPS_STUDENTS_COL_WIDTH - MARKBOOK_FOR_GROUPS_SCROLL_ARROWS_WIDTH
    }px !important`,
    backgroundColor: 'transparent',
    padding: theme.spacing(0, 0.75),
    height: MARKBOOK_FOR_GROUPS_SCROLL_ARROWS_HEIGHT,
  },
  '.MuiStack-root.arrowRight': {
    right: 0,
    background: 'linear-gradient(90deg, transparent 0%, white 30%, white 100%)',
    padding: theme.spacing(0, 0.75),
    height: MARKBOOK_FOR_GROUPS_SCROLL_ARROWS_HEIGHT,
  },
});

export const createMarkbookForAssessmentPreviewGridContainerSx =
  (): SxProps<Theme> => (theme: Theme) => ({
    borderRadius: 0,
  });

export const createMarkbookDataGridSx = (theme: Theme) => ({
  '.MuiDataGrid-pinnedColumnHeaders': {
    backgroundColor: theme.palette.background.paper,
    '&>div': {
      height: '100%',
    },
  },
  '.MuiDataGrid-columnHeaderTitleContainer.MuiDataGrid-withBorderColor': {
    whiteSpace: 'normal',
    border: 'none',
  },
  '.MuiDataGrid-columnHeaders': {
    overflow: 'visible',
  },
  '& .MuiDataGrid-cell--editing': {
    '&.gradeCell': {
      borderLeft: 'none',
      borderBottom: theme.mixins.borderValue(),
    },
    '&.scoreCell': {
      padding: 0,
      '&.MuiDataGrid-cell:focus-within': {
        overflow: 'visible',
        outlineColor: theme.palette.common.light3,
      },
    },
  },
});

export const createMarkbookForGroupDataGridSx = (): SxProps<Theme> => (theme: Theme) => ({
  ...createMarkbookDataGridSx(theme),
  '.assessmentInfoHeaderCell': {
    borderRight: theme.mixins.borderValue(),
    '&.MuiDataGrid-columnHeader.MuiDataGrid-withBorderColor:last-child': {
      borderRight: theme.mixins.borderValue(),
    },
    ' .MuiDataGrid-columnHeaderTitleContainerContent': {
      height: '100%',
    },
  },
  '.methodTypeCell': {
    minHeight: MARKBOOK_FOR_GROUP_METHOD_TYPE_HEADER_HEIGHT,
  },
  '.studentsHeaderCell': {
    minHeight: MARKBOOK_FOR_GROUP_STUDENTS_HEADER_HEIGHT,
  },
  '.withBottomBorder': {
    borderBottom: theme.mixins.borderValue(),
  },
  '.MuiDataGrid-pinnedColumns': {
    '.categoryNameCell, .loadMoreCell, .methodTypeCell, .scoreCell, .gradeCell, .loadingNextPageCell':
      {
        backgroundColor: theme.palette.background.paper,
        outline: 'none',
      },
  },
  ' .MuiDataGrid-row': {
    '&:not(.MuiDataGrid-row--dynamicHeight)>.studentNameCell': {
      overflow: 'visible',
    },
    '.studentNameCell': {
      borderRight: theme.mixins.borderValue(),
      backgroundColor: theme.palette.background.paper,
      pr: theme.spacing(1),
      outline: 'none',
    },
    '.scoreCell.hover, .scoreCell.selfHover': {
      '& .MuiInput-input::placeholder': {
        color: theme.palette.primary.main,
      },
    },
    '&:first-of-type': {
      '& .MuiDataGrid-cell--editing.scoreCell': {
        borderTop: theme.mixins.borderControlValue(),
        '& .MuiStack-root': {
          borderTop: 'none',
        },
      },
    },
  },
  ' .MuiDataGrid-withBorderColor': {
    '&.commentCell, &.gradeCell, &.scoreCell': {
      borderRight: theme.mixins.borderValue(),
    },
  },
});

export const createMarkbookForGroupsDataGridSx = (): SxProps<Theme> => (theme: Theme) => ({
  ...createMarkbookDataGridSx(theme),

  '.assessmentInfoHeaderCell': {
    ' .MuiDataGrid-columnHeaderTitleContainerContent': {
      height: '100%',
    },
    '&:not(:last-child)': {
      borderRight: theme.mixins.borderValue(),
    },
  },
  '.averageCell': {
    borderRight: theme.mixins.borderValue(),
  },
  '& .withTopBorder': {
    borderTop: `${theme.mixins.borderValue()} !important`,
  },
  ' .MuiDataGrid-row': {
    '&:not(.MuiDataGrid-row--dynamicHeight)>.studentNameCell': {
      overflow: 'visible',
    },
    '.studentNameCell': {
      borderRight: theme.mixins.borderValue(),
      backgroundColor: theme.palette.background.paper,
      pr: theme.spacing(1),
      outline: 'none',
    },
    '&:first-of-type': {
      '& .MuiDataGrid-cell--editing.scoreCell': {
        borderTop: theme.mixins.borderControlValue(),
        '& .MuiStack-root': {
          borderTop: 'none',
        },
      },
    },
    '.loadingNextPageCell:not(:last-of-type)': {
      borderRight: theme.mixins.borderValue(),
    },
  },
  '.MuiDataGrid-cell[data-field="baseline"]:not(.methodTypeCell), .MuiDataGrid-columnHeader[data-fields="|-baseline-|"]':
    {
      borderLeft: theme.mixins.borderValue(),
      borderLeftColor: theme.palette.primary.main,
      borderRight: theme.mixins.borderValue(),
      borderRightColor: theme.palette.primary.main,
    },
  '.MuiDataGrid-pinnedColumns': {
    '.categoryNameCell, .loadMoreCell, .methodTypeCell, .averageCategoryCell, .scoreCell, .averageCell, .gradeCell, .loadingNextPageCell':
      {
        backgroundColor: theme.palette.background.paper,
        outline: 'none',
      },
    '.loadingNextPageCell': {
      borderRight: theme.mixins.borderValue(),
    },
  },
  '.subjectHeaderCell.withRightBorder': {
    borderRight: theme.mixins.borderValue(),
  },

  '.averageHeaderCell': {
    borderTop: theme.mixins.borderValue(),
    borderRight: theme.mixins.borderValue(),
  },

  '.subjectHeaderCell.overflowVisible': {
    '& .MuiDataGrid-columnHeaderTitleContainer': {
      overflow: 'visible !important',
      '& .MuiDataGrid-columnHeaderTitleContainerContent': {
        overflow: 'visible !important',
      },
    },
  },

  ' .MuiDataGrid-withBorderColor:not(:last-child)': {
    '&.commentCell, &.gradeCell, &.scoreCell': {
      borderRight: theme.mixins.borderValue(),
    },
  },
});

export const createMarkbookForAssessmentPreviewDataGridSx =
  (): SxProps<Theme> => (theme: Theme) => ({
    '.MuiDataGrid-virtualScroller': {
      pb: theme.spacing(2.5),
    },
    '.MuiDataGrid-pinnedColumns': {
      overflow: 'visible',
      '& .MuiDataGrid-row': {
        '.studentNameCell': {
          borderRight: theme.mixins.borderValue(),
          outline: 'none',
        },
        '&:first-child': {
          borderTopLeftRadius: theme.shape.borderRadius,
        },
        '&:last-child': {
          borderBottomLeftRadius: theme.shape.borderRadius,
        },
      },
    },
    '.MuiDataGrid-virtualScrollerRenderZone': {
      '& .MuiDataGrid-row': {
        '&:first-child': {
          borderTopRightRadius: theme.shape.borderRadius,
        },
        '&:last-child': {
          borderBottomRightRadius: theme.shape.borderRadius,
        },
      },
    },
    ' .MuiDataGrid-row': {
      '&:not(.MuiDataGrid-row--dynamicHeight)>.studentNameCell': {
        overflow: 'visible',
      },
      backgroundColor: theme.palette.background.paper,
      zIndex: -1,
      '&.Mui-hovered': {
        '.studentNameCell': {
          '.MuiTypography-root': {
            color: theme.palette.primary.main,
          },
        },
        '.scoreCell': {
          '& .MuiInput-input::placeholder': {
            color: theme.palette.primary.main,
          },
        },
      },
      ' .MuiDataGrid-withBorderColor': {
        '&.commentCell, &.gradeCell ,&.scoreCell': {
          '&:not(:last-child)': {
            borderRight: theme.mixins.borderValue(),
          },
        },
        '&.MuiDataGrid-cell--editing': {
          '&.commentCell': {
            borderRight: theme.mixins.borderValue(),
          },
          '&.gradeCell': {
            borderLeft: 'none',
            borderBottom: theme.mixins.borderValue(),
          },
          '&.scoreCell': {
            paddingX: 0,
            '&.MuiDataGrid-cell:focus-within': {
              overflow: 'visible',
              outlineColor: theme.palette.common.light3,
              '& .MuiStack-root': {
                borderColor: 'transparent',
              },
            },
          },
        },
      },
    },
  });

export const createGradeDropdownSx = (): Record<string, any> => ({
  '.MuiPaper-root': {
    ml: '-1px',
    minWidth: ASSESSMENT_COL_WIDTH,
  },
});

export const ASSESSMENT_DATA_GRID_PROPS: AssessmentDataGridProps = {
  rowHeight: ASSESSMENT_ROW_HEIGHT,
  columnsToPin: [STUDENT_COL_FIELD_KEY],
  withDataGridHover: false,
  noNavigationOnFirstCol: true,
  headerCellSelector: '.assessmentInfoHeaderCell',
};

const getRowHeight = (params: GridRowHeightParams) => ASSESSMENT_ROW_HEIGHT;

export const MARKBOOK_FOR_GROUPS_DATA_GRID_PROPS: MarkbookForGroupsDataGridProps = {
  ...ASSESSMENT_DATA_GRID_PROPS,
  getRowHeight,
};

export const ASSESSMENT_SCROLL_CONFIG: AssessmentScrollConfig = {
  threshold: 3,
  colWidth: ASSESSMENT_COL_WIDTH,
};

export const shouldShowScrollArrows = (assessmentCount: number): boolean => {
  return assessmentCount > ASSESSMENT_SCROLL_CONFIG.threshold;
};

export type MarkbookForGroupStyling = ReturnType<typeof useMarkbookForGroupStyling>;

export const useMarkbookForGroupStyling = () => {
  const studentNameCellWidth = useMarkbookForGroupStudentNameCellWidth();

  return {
    studentNameCellWidth,
    gridContainerSx: createMarkbookForGroupGridContainerSx(),
    dataGridSx: createMarkbookForGroupDataGridSx(),
    studentCellSx: createStudentNameCellSx(),
    gradeDropdownSx: createGradeDropdownSx(),
    constants: {
      COL_WIDTH: ASSESSMENT_COL_WIDTH,
      ROW_HEIGHT: ASSESSMENT_ROW_HEIGHT,
      STUDENT_COL_FIELD_KEY,
    },
    cssClasses: ASSESSMENT_CSS_CLASSES,
    dataGridProps: ASSESSMENT_DATA_GRID_PROPS,
  };
};

export const useMarkbookForGroupsStyling = () => {
  return {
    studentNameCellWidth: MARKBOOK_FOR_GROUPS_STUDENTS_COL_WIDTH,
    gridContainerSx: createMarkbookForGroupsGridContainerSx(),
    dataGridSx: createMarkbookForGroupsDataGridSx(),
    studentCellSx: createMarkbookForGroupsStudentNameCellSx(),
    gradeDropdownSx: createGradeDropdownSx(),
    constants: {
      COL_WIDTH: ASSESSMENT_COL_WIDTH,
      ROW_HEIGHT: ASSESSMENT_ROW_HEIGHT,
      STUDENT_COL_FIELD_KEY,
    },
    cssClasses: ASSESSMENT_CSS_CLASSES,
    dataGridProps: MARKBOOK_FOR_GROUPS_DATA_GRID_PROPS,
  };
};

export const useMarkbookForAssessmentPreviewStyling = () => {
  return {
    studentNameCellWidth: MARKBOOK_FOR_ASSESSMENT_PREVIEW_STUDENTS_COL_WIDTH,
    gridContainerSx: createMarkbookForAssessmentPreviewGridContainerSx(),
    dataGridSx: createMarkbookForAssessmentPreviewDataGridSx(),
    studentCellSx: createStudentNameCellSx(),
    gradeDropdownSx: createGradeDropdownSx(),
    constants: {
      COL_WIDTH: ASSESSMENT_COL_WIDTH,
      ROW_HEIGHT: ASSESSMENT_ROW_HEIGHT,
      STUDENT_COL_FIELD_KEY,
    },
    cssClasses: ASSESSMENT_CSS_CLASSES,
    dataGridProps: ASSESSMENT_DATA_GRID_PROPS,
  };
};
